import { useEffect, useRef } from 'react';
import { driver, type DriveStep } from 'driver.js';
import 'driver.js/dist/driver.css';
import { useTour, useTooltipContext } from './TooltipContext';
import { tooltipsApi } from '@/api/endpoints/admin';

type GuidedTourProps = {
  tourName: string;
  /** Auto-start when progress is incomplete and steps exist. */
  autoStart?: boolean;
  onClose?: () => void;
};

/**
 * Spotlight guided tour powered by driver.js.
 * Steps come from CMS tooltip rows grouped by tourName (ordered by tourOrder).
 * Steps with a targetSelector spotlight that element; others use a centered popover.
 * Progress persists via /api/tooltips/tours/:name/progress.
 */
export function GuidedTour({ tourName, autoStart = false, onClose }: GuidedTourProps) {
  const steps = useTour(tourName);
  const { isLoading: tooltipsLoading } = useTooltipContext();
  const startedRef = useRef(false);

  useEffect(() => {
    // Wait for CMS tooltips to load so we don't permanently skip an empty first paint.
    if (!autoStart || tooltipsLoading || steps.length === 0 || startedRef.current) return;

    let cancelled = false;
    let activeDriver: ReturnType<typeof driver> | null = null;

    async function start(): Promise<void> {
      let startIndex = 0;
      try {
        const progress = await tooltipsApi.tourProgress(tourName);
        if (cancelled) return;
        if (progress.completed) return;
        startIndex = Math.max(0, Math.min(progress.lastStep, steps.length - 1));
      } catch {
        // No saved progress — start at 0.
      }

      if (cancelled) return;
      startedRef.current = true;

      const prefersReduced =
        typeof window !== 'undefined' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const driveSteps: DriveStep[] = steps.map((step) => {
        const hasTarget =
          Boolean(step.targetSelector) &&
          typeof document !== 'undefined' &&
          Boolean(document.querySelector(step.targetSelector!));

        return {
          element: hasTarget ? step.targetSelector! : undefined,
          popover: {
            title: step.title ?? undefined,
            description: step.body,
            side:
              step.placement === 'left' ||
              step.placement === 'right' ||
              step.placement === 'top' ||
              step.placement === 'bottom'
                ? step.placement
                : 'bottom',
            align: 'start',
          },
        };
      });

      activeDriver = driver({
        showProgress: true,
        animate: !prefersReduced,
        allowClose: true,
        overlayOpacity: 0.55,
        stagePadding: 8,
        stageRadius: 8,
        nextBtnText: 'Next',
        prevBtnText: 'Back',
        doneBtnText: 'Finish',
        steps: driveSteps,
        onDestroyStarted: () => {
          const idx = activeDriver?.getActiveIndex() ?? 0;
          // Closing or finishing both end auto-start. Incomplete progress still
          // records lastStep so a manual restart can resume.
          void tooltipsApi
            .setTourProgress(tourName, {
              completed: true,
              lastStep: idx,
            })
            .catch(() => {
              /* best-effort */
            });
          activeDriver?.destroy();
          onClose?.();
        },
        onNextClick: (_el, _step, opts) => {
          const idx = opts.driver.getActiveIndex() ?? 0;
          const isLast = idx >= steps.length - 1;
          if (isLast) {
            void tooltipsApi
              .setTourProgress(tourName, {
                completed: true,
                lastStep: idx,
              })
              .catch(() => {
                /* best-effort */
              });
            opts.driver.destroy();
            onClose?.();
            return;
          }
          void tooltipsApi
            .setTourProgress(tourName, {
              completed: false,
              lastStep: idx + 1,
            })
            .catch(() => {
              /* best-effort */
            });
          opts.driver.moveNext();
        },
        onPrevClick: (_el, _step, opts) => {
          opts.driver.movePrevious();
        },
        onCloseClick: (_el, _step, opts) => {
          const idx = opts.driver.getActiveIndex() ?? 0;
          void tooltipsApi
            .setTourProgress(tourName, {
              completed: true,
              lastStep: idx,
            })
            .catch(() => {
              /* best-effort */
            });
          opts.driver.destroy();
          onClose?.();
        },
      });

      activeDriver.drive(startIndex);
    }

    void start();

    return () => {
      cancelled = true;
      activeDriver?.destroy();
    };
  }, [autoStart, onClose, steps, tourName, tooltipsLoading]);

  return null;
}
